import { useCallback, useMemo, useSyncExternalStore } from "react";

const STORAGE_KEY = "bastion.starredChatSessionIds";
const SERVER_IDS: string[] = [];
const listeners = new Set<() => void>();
let cache: string[] | null = null;

function parseStoredIds(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const x of parsed) {
      if (typeof x !== "string") continue;
      const id = x.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out;
  } catch {
    return [];
  }
}

function loadStarredIds(): string[] {
  if (cache) return cache;
  try {
    cache = typeof window === "undefined"
      ? [] : parseStoredIds(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    cache = [];
  }
  return cache;
}

function persistStarredIds(ids: string[]) {
  cache = ids;
  try {
    if (typeof window !== "undefined") window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // Keep stars in memory when storage is blocked or full.
  }
  for (const listener of listeners) listener();
}

function onStorage(event: StorageEvent) {
  if (event.key !== STORAGE_KEY && event.key !== null) return;
  cache = parseStoredIds(event.newValue);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  if (listeners.size === 1) window.addEventListener("storage", onStorage);
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) {
      window.removeEventListener("storage", onStorage);
      cache = null;
    }
  };
}

export function useStarredChats() {
  const starredSessionIds = useSyncExternalStore(subscribe, loadStarredIds, () => SERVER_IDS);

  const isStarred = useCallback(
    (sessionId: string) => starredSessionIds.includes(sessionId),
    [starredSessionIds],
  );

  const toggleStar = useCallback(
    (sessionId: string) => {
      const trimmed = sessionId.trim();
      if (!trimmed) return;
      const current = loadStarredIds();
      const has = current.includes(trimmed);
      const next = has
        ? current.filter((id) => id !== trimmed)
        : [trimmed, ...current.filter((id) => id !== trimmed)];
      persistStarredIds(next);
    },
    [],
  );

  const starredIdSet = useMemo(() => new Set(starredSessionIds), [starredSessionIds]);

  return {
    starredSessionIds,
    starredIdSet,
    isStarred,
    toggleStar,
  };
}
